import { lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';

import { AdminLayout } from '@/components/layout/AdminLayout';
import { ConfirmProvider } from '@/components/ui/confirm-dialog';
import { AuthProvider } from '@/lib/auth';
import { Login } from '@/pages/Login';
import { SandDollarsRedirect } from '@/pages/sand-dollars/SandDollarsRedirect';
import { RealtimeProvider } from '@/realtime/RealtimeProvider';
import { ProtectedRoute } from '@/routes/ProtectedRoute';

const SparkyClassroom = lazy(() =>
  import('@/pages/sparky/SparkyClassroom').then(m => ({ default: m.SparkyClassroom }))
);
const LessonReportPage = lazy(() =>
  import('@/pages/sparky/LessonReportPage').then(m => ({ default: m.LessonReportPage }))
);
const Dashboard = lazy(() => import('@/pages/dashboard').then(m => ({ default: m.Dashboard })));
const NewsList = lazy(() => import('@/pages/news/NewsList').then(m => ({ default: m.NewsList })));
const NewsEdit = lazy(() => import('@/pages/news/NewsEdit').then(m => ({ default: m.NewsEdit })));
const CalendarList = lazy(() => import('@/pages/calendar/CalendarList').then(m => ({ default: m.CalendarList })));
const ThaiEventsList = lazy(() =>
  import('@/pages/thai-events/ThaiEventsList').then(m => ({ default: m.ThaiEventsList }))
);
const AnnouncementsList = lazy(() =>
  import('@/pages/announcements/AnnouncementsList').then(m => ({ default: m.AnnouncementsList }))
);
const WeeklyMenusList = lazy(() =>
  import('@/pages/weekly-menus/WeeklyMenusList').then(m => ({ default: m.WeeklyMenusList }))
);
const MyEntries = lazy(() => import('@/pages/newsletter/MyEntries').then(m => ({ default: m.MyEntries })));
const CommunicationsList = lazy(() =>
  import('@/pages/communications/CommunicationsList').then(m => ({ default: m.CommunicationsList }))
);
const CommunicationEditor = lazy(() =>
  import('@/pages/communications/CommunicationEditor').then(m => ({ default: m.CommunicationEditor }))
);
const CommunicationDetail = lazy(() =>
  import('@/pages/communications/CommunicationDetail').then(m => ({ default: m.CommunicationDetail }))
);
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const StatisticsPage = lazy(() =>
  import('@/pages/statistics/StatisticsPage').then(m => ({ default: m.StatisticsPage }))
);
const InquiriesPage = lazy(() => import('@/pages/inquiries/InquiriesPage').then(m => ({ default: m.InquiriesPage })));
const ContactInquiryDetail = lazy(() =>
  import('@/pages/contact-inquiries/ContactInquiryDetail').then(m => ({ default: m.ContactInquiryDetail }))
);
const TourRequestDetail = lazy(() =>
  import('@/pages/tour-requests/TourRequestDetail').then(m => ({ default: m.TourRequestDetail }))
);
const MyLeaveRequests = lazy(() =>
  import('@/pages/leave-requests/MyLeaveRequests').then(m => ({ default: m.MyLeaveRequests }))
);
const LeaveRequestsList = lazy(() =>
  import('@/pages/leave-requests/LeaveRequestsList').then(m => ({ default: m.LeaveRequestsList }))
);
const LeaveRequestDetail = lazy(() =>
  import('@/pages/leave-requests/LeaveRequestDetail').then(m => ({ default: m.LeaveRequestDetail }))
);
const MyLeaveRequestDetail = lazy(() =>
  import('@/pages/leave-requests/MyLeaveRequestDetail').then(m => ({ default: m.MyLeaveRequestDetail }))
);
const MyTickets = lazy(() => import('@/pages/it-tickets/MyTickets').then(m => ({ default: m.MyTickets })));
const TicketDetail = lazy(() => import('@/pages/it-tickets/TicketDetail').then(m => ({ default: m.TicketDetail })));
const TicketBoard = lazy(() => import('@/pages/it-tickets/TicketBoard').then(m => ({ default: m.TicketBoard })));
const TicketArchive = lazy(() => import('@/pages/it-tickets/TicketArchive').then(m => ({ default: m.TicketArchive })));
const ItAssetList = lazy(() => import('@/pages/it-assets/ItAssetList').then(m => ({ default: m.ItAssetList })));
const ItAssetLabels = lazy(() => import('@/pages/it-assets/ItAssetLabels').then(m => ({ default: m.ItAssetLabels })));
const ItAssetDetail = lazy(() => import('@/pages/it-assets/ItAssetDetail').then(m => ({ default: m.ItAssetDetail })));
const MyBookings = lazy(() => import('@/pages/bookings/MyBookings').then(m => ({ default: m.MyBookings })));
const AllBookings = lazy(() => import('@/pages/bookings/AllBookings').then(m => ({ default: m.AllBookings })));
const TabletPoolSettings = lazy(() =>
  import('@/pages/bookings/TabletPoolSettings').then(m => ({ default: m.TabletPoolSettings }))
);
const BookingDetail = lazy(() => import('@/pages/bookings/BookingDetail').then(m => ({ default: m.BookingDetail })));
const ImmigrationComplianceList = lazy(() =>
  import('@/pages/immigration-compliance/ImmigrationComplianceList').then(m => ({
    default: m.ImmigrationComplianceList,
  }))
);
const ImmigrationComplianceSettings = lazy(() =>
  import('@/pages/immigration-compliance/ImmigrationComplianceSettings').then(m => ({
    default: m.ImmigrationComplianceSettings,
  }))
);
const StaffDetail = lazy(() => import('@/pages/staff/StaffDetail').then(m => ({ default: m.StaffDetail })));
const TeachersPage = lazy(() => import('@/pages/teachers/TeachersPage').then(m => ({ default: m.TeachersPage })));
const UsersList = lazy(() => import('@/pages/users/UsersList').then(m => ({ default: m.UsersList })));
const ParentsList = lazy(() => import('@/pages/parents/ParentsList').then(m => ({ default: m.ParentsList })));
const ParentDetail = lazy(() => import('@/pages/parents/ParentDetail').then(m => ({ default: m.ParentDetail })));
const StudentsRouter = lazy(() => import('@/pages/students/StudentsRouter').then(m => ({ default: m.StudentsRouter })));
const ReportCardsPage = lazy(() =>
  import('@/pages/report-cards/ReportCardsPage').then(m => ({ default: m.ReportCardsPage }))
);
const StudentDetailRouter = lazy(() =>
  import('@/pages/students/StudentDetailRouter').then(m => ({ default: m.StudentDetailRouter }))
);
const CredentialExport = lazy(() =>
  import('@/pages/students/CredentialExport').then(m => ({ default: m.CredentialExport }))
);
const QuickAward = lazy(() => import('@/pages/sand-dollars/QuickAward').then(m => ({ default: m.QuickAward })));
const SandDollarsPage = lazy(() =>
  import('@/pages/sand-dollars/SandDollarsPage').then(m => ({ default: m.SandDollarsPage }))
);
const SandDollarsConfig = lazy(() =>
  import('@/pages/sand-dollars/SandDollarsConfig').then(m => ({ default: m.SandDollarsConfig }))
);
const CelebrationDetail = lazy(() =>
  import('@/pages/sand-dollars/CelebrationDetail').then(m => ({ default: m.CelebrationDetail }))
);
const HousePoints = lazy(() => import('@/pages/sand-dollars/HousePoints').then(m => ({ default: m.HousePoints })));
const SchoolYearsPage = lazy(() =>
  import('@/pages/school-years/SchoolYearsPage').then(m => ({ default: m.SchoolYearsPage }))
);
const LogsPage = lazy(() => import('@/pages/logs/LogsPage').then(m => ({ default: m.LogsPage })));
const StudentAbsencesList = lazy(() =>
  import('@/pages/student-absences/StudentAbsencesList').then(m => ({ default: m.StudentAbsencesList }))
);
const StudentAbsenceDetail = lazy(() =>
  import('@/pages/student-absences/StudentAbsenceDetail').then(m => ({ default: m.StudentAbsenceDetail }))
);
const LessonExcusalDetail = lazy(() =>
  import('@/pages/student-absences/LessonExcusalDetail').then(m => ({ default: m.LessonExcusalDetail }))
);
const ArrivalsDismissalsList = lazy(() =>
  import('@/pages/arrivals-dismissals/ArrivalsDismissalsList').then(m => ({ default: m.ArrivalsDismissalsList }))
);
const EarlyDismissalDetail = lazy(() =>
  import('@/pages/early-dismissals/EarlyDismissalDetail').then(m => ({ default: m.EarlyDismissalDetail }))
);
const LateArrivalDetail = lazy(() =>
  import('@/pages/late-arrivals/LateArrivalDetail').then(m => ({ default: m.LateArrivalDetail }))
);
const IncidentDetail = lazy(() =>
  import('@/pages/incidents/IncidentDetail').then(m => ({ default: m.IncidentDetail }))
);
const SstPage = lazy(() => import('@/pages/sst/SstPage').then(m => ({ default: m.SstPage })));
const LunchDetentionDetail = lazy(() =>
  import('@/pages/sst/LunchDetentionDetail').then(m => ({ default: m.LunchDetentionDetail }))
);
const SuspensionDetail = lazy(() =>
  import('@/pages/sst/suspensions/SuspensionDetail').then(m => ({ default: m.SuspensionDetail }))
);
const AttendanceDaily = lazy(() =>
  import('@/pages/attendance/AttendanceDaily').then(m => ({ default: m.AttendanceDaily }))
);
const AttendanceReport = lazy(() =>
  import('@/pages/attendance/AttendanceReport').then(m => ({ default: m.AttendanceReport }))
);
const AttendancePersonHistory = lazy(() =>
  import('@/pages/attendance/AttendancePersonHistory').then(m => ({ default: m.AttendancePersonHistory }))
);
const Checkout = lazy(() => import('@/pages/attendance/Checkout').then(m => ({ default: m.Checkout })));
const TimetableSetup = lazy(() =>
  import('@/pages/timetable/TimetableSetup').then(m => ({ default: m.TimetableSetup }))
);
const TimetablePage = lazy(() => import('@/pages/timetable/TimetablePage').then(m => ({ default: m.TimetablePage })));
const MyClassesPage = lazy(() => import('@/pages/attendance/MyClassesPage').then(m => ({ default: m.MyClassesPage })));
const ClassSstObservationsPage = lazy(() =>
  import('@/pages/attendance/ClassSstObservationsPage').then(m => ({ default: m.ClassSstObservationsPage }))
);
const MarkSheetPage = lazy(() =>
  import('@/pages/attendance/class-tests/MarkSheetPage').then(m => ({ default: m.MarkSheetPage }))
);
const OnlineBuilderPage = lazy(() =>
  import('@/pages/attendance/class-tests/online/OnlineBuilderPage').then(m => ({ default: m.OnlineBuilderPage }))
);
const OnlineRunPage = lazy(() =>
  import('@/pages/attendance/class-tests/online/OnlineRunPage').then(m => ({ default: m.OnlineRunPage }))
);
const PeriodAttendanceAdmin = lazy(() =>
  import('@/pages/attendance/PeriodAttendanceAdmin').then(m => ({ default: m.PeriodAttendanceAdmin }))
);
const ImportPage = lazy(() => import('@/pages/import/ImportPage').then(m => ({ default: m.ImportPage })));
const MedicalHubPage = lazy(() => import('@/pages/medical/MedicalHubPage').then(m => ({ default: m.MedicalHubPage })));
const MedicalSettingsPage = lazy(() =>
  import('@/pages/medical/MedicalSettingsPage').then(m => ({ default: m.MedicalSettingsPage }))
);
const DailyLogPage = lazy(() => import('@/pages/medical/DailyLogPage').then(m => ({ default: m.DailyLogPage })));
const InfirmaryVisitDetail = lazy(() =>
  import('@/pages/medical/InfirmaryVisitDetail').then(m => ({ default: m.InfirmaryVisitDetail }))
);
const AllergyChangeRequestsList = lazy(() =>
  import('@/pages/allergy-change-requests/AllergyChangeRequestsList').then(m => ({
    default: m.AllergyChangeRequestsList,
  }))
);
const AllergyChangeRequestDetail = lazy(() =>
  import('@/pages/allergy-change-requests/AllergyChangeRequestDetail').then(m => ({
    default: m.AllergyChangeRequestDetail,
  }))
);
const TemperaturePage = lazy(() =>
  import('@/pages/temperature/TemperaturePage').then(m => ({ default: m.TemperaturePage }))
);
const CafeteriaPage = lazy(() => import('@/pages/cafeteria/CafeteriaPage').then(m => ({ default: m.CafeteriaPage })));
const CafeteriaPos = lazy(() => import('@/pages/cafeteria/CafeteriaPos').then(m => ({ default: m.CafeteriaPos })));
const ShopCatalog = lazy(() => import('@/pages/shop/ShopCatalog').then(m => ({ default: m.ShopCatalog })));
const TuitionPage = lazy(() => import('@/pages/tuition/TuitionPage').then(m => ({ default: m.TuitionPage })));
const ShopPos = lazy(() => import('@/pages/shop/ShopPos').then(m => ({ default: m.ShopPos })));
const ShopInventory = lazy(() => import('@/pages/shop/ShopInventory').then(m => ({ default: m.ShopInventory })));
const ShopReports = lazy(() => import('@/pages/shop/ShopReports').then(m => ({ default: m.ShopReports })));
const IntegrationSettings = lazy(() =>
  import('@/pages/integration/IntegrationSettings').then(m => ({ default: m.IntegrationSettings }))
);
const BellSchedulePage = lazy(() =>
  import('@/pages/integration/BellSchedulePage').then(m => ({ default: m.BellSchedulePage }))
);
const PresencePage = lazy(() => import('@/pages/presence/PresencePage').then(m => ({ default: m.PresencePage })));
const MyProfilePage = lazy(() => import('@/pages/profile/MyProfilePage').then(m => ({ default: m.MyProfilePage })));
const EdpGuide = lazy(() => import('@/pages/edp/EdpGuide').then(m => ({ default: m.EdpGuide })));
const ProposeCourse = lazy(() => import('@/pages/edp/ProposeCourse').then(m => ({ default: m.ProposeCourse })));
const MyEdpPage = lazy(() => import('@/pages/edp/MyEdpPage').then(m => ({ default: m.MyEdpPage })));
const EdpPage = lazy(() => import('@/pages/edp/EdpPage').then(m => ({ default: m.EdpPage })));
const ProposalDetail = lazy(() => import('@/pages/edp/ProposalDetail').then(m => ({ default: m.ProposalDetail })));
const CoursePlacement = lazy(() => import('@/pages/edp/CoursePlacement').then(m => ({ default: m.CoursePlacement })));
const EdpAfternoon = lazy(() => import('@/pages/edp/EdpAfternoon').then(m => ({ default: m.EdpAfternoon })));
const EdpRegister = lazy(() => import('@/pages/edp/EdpRegister').then(m => ({ default: m.EdpRegister })));

/** Preserves the :id param when redirecting the old student-directory detail URL to its merged replacement. */
function RedirectToStudent() {
  const { id } = useParams();
  return <Navigate to={`/students/${id}`} replace />;
}

/** Preserves the :id param when redirecting the old incidents detail URL into the SST page. */
function IncidentRedirect() {
  const { id } = useParams();
  return <Navigate to={`/sst/incidents/${id}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <RealtimeProvider>
                    <AdminLayout />
                  </RealtimeProvider>
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route
                path="news"
                element={
                  <ProtectedRoute adminOnly>
                    <NewsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="news/:id/edit"
                element={
                  <ProtectedRoute adminOnly>
                    <NewsEdit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="newsletter"
                element={
                  <ProtectedRoute>
                    <MyEntries />
                  </ProtectedRoute>
                }
              />
              <Route
                path="communications"
                element={
                  <ProtectedRoute module="communications">
                    <CommunicationsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="communications/new"
                element={
                  <ProtectedRoute module="communications">
                    <CommunicationEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="communications/:id"
                element={
                  <ProtectedRoute module="communications">
                    <CommunicationDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="calendar-admin"
                element={
                  <ProtectedRoute module="calendar">
                    <CalendarList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="thai-events"
                element={
                  <ProtectedRoute module="thai-events">
                    <ThaiEventsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="announcements"
                element={
                  <ProtectedRoute module="announcements">
                    <AnnouncementsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="weekly-menus"
                element={
                  <ProtectedRoute module="weekly-menus">
                    <WeeklyMenusList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="inquiries"
                element={
                  <ProtectedRoute anyModule={['contact-inquiries', 'tour-requests']}>
                    <InquiriesPage />
                  </ProtectedRoute>
                }
              />
              <Route path="contact-inquiries" element={<Navigate to="/inquiries?tab=contact" replace />} />
              <Route
                path="inquiries/contact/:id"
                element={
                  <ProtectedRoute module="contact-inquiries">
                    <ContactInquiryDetail />
                  </ProtectedRoute>
                }
              />
              <Route path="tour-requests" element={<Navigate to="/inquiries?tab=tours" replace />} />
              <Route
                path="inquiries/tours/:id"
                element={
                  <ProtectedRoute module="tour-requests">
                    <TourRequestDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-leaves"
                element={
                  <ProtectedRoute>
                    <MyLeaveRequests />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-leaves/:id"
                element={
                  <ProtectedRoute>
                    <MyLeaveRequestDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="it-tickets/mine"
                element={
                  <ProtectedRoute roles={['admin', 'teacher', 'employee']}>
                    <MyTickets />
                  </ProtectedRoute>
                }
              />
              <Route
                path="it-tickets"
                element={
                  <ProtectedRoute module="it-support">
                    <TicketBoard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="it-tickets/archive"
                element={
                  <ProtectedRoute module="it-support">
                    <TicketArchive />
                  </ProtectedRoute>
                }
              />
              <Route
                path="it-tickets/:id"
                element={
                  <ProtectedRoute roles={['admin', 'teacher', 'employee']}>
                    <TicketDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="it-assets"
                element={
                  <ProtectedRoute module="it-support">
                    <ItAssetList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="it-assets/labels"
                element={
                  <ProtectedRoute module="it-support">
                    <ItAssetLabels />
                  </ProtectedRoute>
                }
              />
              <Route
                path="it-assets/:id"
                element={
                  <ProtectedRoute module="it-support">
                    <ItAssetDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="edp/about"
                element={
                  <ProtectedRoute>
                    <EdpGuide />
                  </ProtectedRoute>
                }
              />
              <Route
                path="edp/propose"
                element={
                  <ProtectedRoute>
                    <ProposeCourse />
                  </ProtectedRoute>
                }
              />
              <Route
                path="edp/mine"
                element={
                  <ProtectedRoute>
                    <MyEdpPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="edp"
                element={
                  <ProtectedRoute module="edp">
                    <EdpPage />
                  </ProtectedRoute>
                }
              />
              <Route path="edp/proposals" element={<Navigate to="/edp?tab=proposals" replace />} />
              <Route
                path="edp/proposals/:id"
                element={
                  <ProtectedRoute>
                    <ProposalDetail />
                  </ProtectedRoute>
                }
              />
              <Route path="edp/rounds" element={<Navigate to="/school-years?tab=edp-rounds" replace />} />
              <Route path="edp/registrations" element={<Navigate to="/edp?tab=registrations" replace />} />
              <Route
                path="edp/courses/:courseId/placement"
                element={
                  <ProtectedRoute module="edp">
                    <CoursePlacement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="edp/courses/:courseId/register"
                element={
                  // Not module-gated, matching the API: an EDP room is supervised by whoever
                  // looks in at 16:20, and a course a part-timer teaches may carry no instructor
                  // account at all (see `edp-attendance.controller.ts`).
                  <ProtectedRoute roles={['admin', 'teacher', 'employee']}>
                    <EdpRegister />
                  </ProtectedRoute>
                }
              />
              <Route
                path="edp/afternoon"
                element={
                  // Not module-gated: every homeroom teacher needs this at 15:30, same as
                  // the API's own `afternoon` endpoint (see `edp.controller.ts`).
                  <ProtectedRoute roles={['admin', 'teacher', 'employee']}>
                    <EdpAfternoon />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bookings/mine"
                element={
                  <ProtectedRoute roles={['admin', 'teacher', 'employee']}>
                    <MyBookings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bookings"
                element={
                  <ProtectedRoute module="bookings">
                    <AllBookings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bookings/tablet-pool"
                element={
                  <ProtectedRoute module="bookings">
                    <TabletPoolSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bookings/:id"
                element={
                  // Deliberately wider than the /bookings list: the people a bookings.created
                  // notification reaches are named in a settings list, not the bookings module
                  // grant, so the page it links to cannot require that grant without dead-ending
                  // the very teacher or employee the notice was sent to.
                  <ProtectedRoute>
                    <BookingDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="student-absences"
                element={
                  <ProtectedRoute>
                    <StudentAbsencesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="student-absences/:id"
                element={
                  <ProtectedRoute>
                    <StudentAbsenceDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="lesson-excusals/:id"
                element={
                  <ProtectedRoute>
                    <LessonExcusalDetail />
                  </ProtectedRoute>
                }
              />
              <Route path="early-dismissals" element={<Navigate to="/arrivals-dismissals" replace />} />
              <Route
                path="early-dismissals/:id"
                element={
                  <ProtectedRoute>
                    <EarlyDismissalDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="arrivals-dismissals"
                element={
                  <ProtectedRoute>
                    <ArrivalsDismissalsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="late-arrivals/:id"
                element={
                  <ProtectedRoute>
                    <LateArrivalDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sst"
                element={
                  <ProtectedRoute>
                    <SstPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sst/incidents/:id"
                element={
                  <ProtectedRoute>
                    <IncidentDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sst/lunch-detentions/:id"
                element={
                  <ProtectedRoute>
                    <LunchDetentionDetail />
                  </ProtectedRoute>
                }
              />
              <Route path="sst/suspensions/mine" element={<Navigate to="/sst?tab=suspensions" replace />} />
              <Route
                path="sst/suspensions/:id"
                element={
                  <ProtectedRoute>
                    <SuspensionDetail />
                  </ProtectedRoute>
                }
              />
              <Route path="incidents" element={<Navigate to="/sst?tab=incidents" replace />} />
              <Route path="incidents/:id" element={<IncidentRedirect />} />
              <Route
                path="attendance"
                element={
                  <ProtectedRoute roles={['admin', 'teacher']}>
                    <AttendanceDaily />
                  </ProtectedRoute>
                }
              />
              <Route
                path="attendance/report"
                element={
                  <ProtectedRoute roles={['admin', 'teacher']}>
                    <AttendanceReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="attendance/person/:userId"
                element={
                  <ProtectedRoute roles={['admin', 'teacher']}>
                    <AttendancePersonHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="attendance/checkout"
                element={
                  <ProtectedRoute roles={['admin', 'teacher']}>
                    <Checkout />
                  </ProtectedRoute>
                }
              />
              <Route
                path="attendance/school-calendar"
                element={<Navigate to="/school-years?tab=school-days" replace />}
              />
              <Route path="attendance/sessions" element={<Navigate to="/school-years?tab=sessions" replace />} />
              <Route
                path="leave-requests"
                element={
                  <ProtectedRoute module="leave-requests">
                    <LeaveRequestsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="immigration-compliance"
                element={
                  <ProtectedRoute module="immigration-compliance">
                    <ImmigrationComplianceList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="immigration-compliance/settings"
                element={
                  <ProtectedRoute module="immigration-compliance">
                    <ImmigrationComplianceSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="staff/:userId"
                element={
                  <ProtectedRoute>
                    <StaffDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="leave-requests/:id"
                element={
                  <ProtectedRoute module="leave-requests">
                    <LeaveRequestDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sparky"
                element={
                  <ProtectedRoute module="sparky">
                    <SparkyClassroom />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sparky/lessons/:lessonId"
                element={
                  <ProtectedRoute module="sparky">
                    <LessonReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="teachers"
                element={
                  <ProtectedRoute module="teachers">
                    <TeachersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <ProtectedRoute module="settings">
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="statistics"
                element={
                  <ProtectedRoute module="statistics">
                    <StatisticsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="analytics" element={<Navigate to="/statistics" replace />} />
              <Route
                path="users"
                element={
                  <ProtectedRoute module="users">
                    <UsersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="logs"
                element={
                  <ProtectedRoute module="audit-log">
                    <LogsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="audit-log" element={<Navigate to="/logs?tab=activity" replace />} />
              <Route
                path="school-years"
                element={
                  <ProtectedRoute anyModule={['school-years']} orAdmin>
                    <SchoolYearsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sand-dollars/config"
                element={
                  <ProtectedRoute module="sand-dollars-config">
                    <SandDollarsConfig />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sand-dollars/house-points"
                element={
                  <ProtectedRoute module="sand-dollars-config">
                    <HousePoints />
                  </ProtectedRoute>
                }
              />
              <Route
                path="students"
                element={
                  <ProtectedRoute roles={['admin', 'teacher', 'employee']}>
                    <StudentsRouter />
                  </ProtectedRoute>
                }
              />
              <Route
                path="students/export"
                element={
                  // An ordinary roster export, open to any admin or teacher: passport numbers and
                  // decrypted platform logins are dropped per column server-side for a caller who
                  // may not have them, so the module gates the columns and not the door.
                  <ProtectedRoute roles={['admin', 'teacher']}>
                    <CredentialExport />
                  </ProtectedRoute>
                }
              />
              <Route path="students/credential-export" element={<Navigate to="/students/export" replace />} />
              <Route
                path="report-cards"
                element={
                  // Not module-gated, unlike `report-periods`/`completeness` behind it: every
                  // teacher owes grades whether or not they hold the `report-cards` module — see
                  // the same reasoning on `nav.edp_afternoon` and `ReportCardsPage` itself.
                  // No `employee` though: `report-card-entry.controller.ts` is @Roles(admin,
                  // teacher), so an employee would reach this page only to 403 on every read and
                  // be told no period is open.
                  <ProtectedRoute roles={['admin', 'teacher']}>
                    <ReportCardsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="parents"
                element={
                  <ProtectedRoute module="parents">
                    <ParentsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="parents/:id"
                element={
                  <ProtectedRoute module="parents">
                    <ParentDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="students/:id"
                element={
                  <ProtectedRoute roles={['admin', 'teacher', 'employee']}>
                    <StudentDetailRouter />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sand-dollars/award"
                element={
                  <ProtectedRoute>
                    <QuickAward />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sand-dollars"
                element={
                  <ProtectedRoute>
                    <SandDollarsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="sand-dollars/awards" element={<SandDollarsRedirect tab="awards" />} />
              <Route
                path="sand-dollars/celebrations/:id"
                element={
                  <ProtectedRoute>
                    <CelebrationDetail />
                  </ProtectedRoute>
                }
              />
              <Route path="sand-dollars/stats" element={<SandDollarsRedirect tab="stats" />} />
              <Route
                path="timetable"
                element={
                  <ProtectedRoute>
                    <TimetablePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="timetable/setup"
                element={
                  <ProtectedRoute module="timetable">
                    <TimetableSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-classes"
                element={
                  <ProtectedRoute>
                    <MyClassesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-classes/sst-observations"
                element={
                  <ProtectedRoute roles={['admin', 'teacher']}>
                    <ClassSstObservationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-classes/tests/:id/marks"
                element={
                  <ProtectedRoute>
                    <MarkSheetPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-classes/tests/:id/online"
                element={
                  <ProtectedRoute>
                    <OnlineBuilderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-classes/tests/:id/run"
                element={
                  <ProtectedRoute>
                    <OnlineRunPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="attendance/period"
                element={
                  <ProtectedRoute roles={['admin', 'teacher']}>
                    <PeriodAttendanceAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="import"
                element={
                  <ProtectedRoute adminOnly>
                    <ImportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="medical"
                element={
                  <ProtectedRoute module="medical">
                    <MedicalHubPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="medical/settings"
                element={
                  <ProtectedRoute module="medical">
                    <MedicalSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="medical/log"
                element={
                  <ProtectedRoute module="medical">
                    <DailyLogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="medical/log/:id"
                element={
                  <ProtectedRoute module="medical">
                    <InfirmaryVisitDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="medical/allergy-change-requests"
                element={
                  <ProtectedRoute module="medical">
                    <AllergyChangeRequestsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="medical/allergy-change-requests/:id"
                element={
                  <ProtectedRoute module="medical">
                    <AllergyChangeRequestDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="temperature"
                element={
                  <ProtectedRoute module="medical">
                    <TemperaturePage />
                  </ProtectedRoute>
                }
              />
              <Route path="student-directory" element={<Navigate to="/students" replace />} />
              <Route path="student-directory/:id" element={<RedirectToStudent />} />
              <Route
                path="cafeteria"
                element={
                  <ProtectedRoute module="cafeteria">
                    <CafeteriaPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="cafeteria/pos"
                element={
                  <ProtectedRoute module="cafeteria">
                    <CafeteriaPos />
                  </ProtectedRoute>
                }
              />
              <Route path="cafeteria/wallets" element={<Navigate to="/cafeteria?tab=wallets" replace />} />
              <Route path="cafeteria/reports" element={<Navigate to="/cafeteria?tab=reports" replace />} />
              <Route
                path="tuition"
                element={
                  <ProtectedRoute module="billing">
                    <TuitionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="shop"
                element={
                  <ProtectedRoute module="shop">
                    <ShopCatalog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="shop/pos"
                element={
                  <ProtectedRoute module="shop">
                    <ShopPos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="shop/inventory"
                element={
                  <ProtectedRoute module="shop">
                    <ShopInventory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="shop/reports"
                element={
                  <ProtectedRoute module="shop">
                    <ShopReports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="integration"
                element={
                  <ProtectedRoute adminOnly>
                    <IntegrationSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="integration/bell-schedule"
                element={
                  <ProtectedRoute adminOnly>
                    <BellSchedulePage />
                  </ProtectedRoute>
                }
              />
              <Route path="integration/log" element={<Navigate to="/logs?tab=door" replace />} />
              <Route
                path="my-profile"
                element={
                  <ProtectedRoute>
                    <MyProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="presence"
                element={
                  // Who is online is the app bar's indicator for every colleague, not an
                  // admin tool — the endpoint behind it already serves teachers and employees.
                  <ProtectedRoute>
                    <PresencePage />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;
